import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import globalUserPage from '../../../../stores/global-user-page';
import { observer } from 'mobx-react-lite';
import { AssignmentWithStudentSubmissionWithCourseDetails } from '../../../../api/types/students/assignments/student-assignments';

export const UserPageStudentAssignments: React.FC = observer(() => {
  const assignments = useMemo(() => {
    return globalUserPage.studentDetails?.assignments ?? [];
  }, [globalUserPage.studentDetails?.assignments]);

  const formatDateTime = (date: Date | undefined) => {
    if (!date) return <span style={{ color: '#999' }}>טרם הוגש</span>;
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const dueDateTemplate = (
    rowData: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    const dueDate = new Date(rowData.dueDate);
    const now = new Date();
    const isOverdue = dueDate < now && !rowData.submittedAt;

    return (
      <span style={{ color: isOverdue ? '#d32f2f' : 'inherit' }}>
        {format(dueDate, 'dd/MM/yyyy')}
      </span>
    );
  };

  const customDueDateTemplate = (
    rowData: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    if (!rowData.customDueDate) {
      return <span style={{ color: '#999' }}>לא קיים</span>;
    }

    const dueDate = new Date(rowData.customDueDate);
    const now = new Date();
    const isOverdue = dueDate < now && !rowData.submittedAt;

    return (
      <span style={{ color: isOverdue ? '#d32f2f' : 'inherit' }}>
        {format(dueDate, 'dd/MM/yyyy')}
      </span>
    );
  };

  const submissionStatusTemplate = (
    rowData: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    const isSubmitted = !!rowData.submittedAt;
    return (
      <span
        style={{
          color: isSubmitted ? '#2e7d32' : '#ed6c02',
          fontWeight: 'bold',
        }}
      >
        {isSubmitted
          ? 'הוגש'
          : rowData.isGradeInsert
            ? 'טרם פורסמו ציונים'
            : 'טרם הוגש'}
      </span>
    );
  };

  const fileUrlTemplate = (
    rowData: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    if (!rowData.studentFileUrl)
      return <span style={{ color: '#999' }}>טרם הוגש</span>;
    return (
      <a
        href={rowData.studentFileUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1976d2', textDecoration: 'underline' }}
      >
        {'הורד קובץ'}
      </a>
    );
  };

  const gradeTemplate = (
    rowData: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    if (rowData.grade === undefined || rowData.grade === null) {
      return <span style={{ color: '#999' }}>טרם הוגש</span>;
    }

    let color = '#000';
    if (rowData.grade >= 90) {
      color = '#2e7d32'; // Green for high grades
    } else if (rowData.grade >= 70) {
      color = '#1976d2'; // Blue for good grades
    } else if (rowData.grade >= 55) {
      color = '#ed6c02'; // Orange for passing grades
    } else {
      color = '#d32f2f'; // Red for failing grades
    }

    return <span style={{ color, fontWeight: 'bold' }}>{rowData.grade}</span>;
  };

  return (
    <div style={{ width: '100%', padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>מטלות</h2>
      <DataTable
        value={assignments}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage="לא נמצאו מטלות"
        style={{ direction: 'rtl' }}
      >
        <Column
          field="programName"
          header="תוכנית"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="courseName"
          header="קורס"
          body={(rowData: AssignmentWithStudentSubmissionWithCourseDetails) => (
            <div>
              {rowData.courseName}
              {rowData.additionalCourseName &&
                ` - ${rowData.additionalCourseName}`}
            </div>
          )}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          body={dueDateTemplate}
          header="תאריך אחרון להגשה"
          sortable
          style={{ textAlign: 'right' }}
        />

        <Column
          body={customDueDateTemplate}
          header="תאריך אחרון להגשה מיוחדת"
          sortable
          style={{ textAlign: 'right' }}
        />

        <Column
          body={submissionStatusTemplate}
          header="סטטוס"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          body={fileUrlTemplate}
          header="קישור למטלה"
          style={{ textAlign: 'right' }}
        />
        <Column
          body={gradeTemplate}
          header="ציון"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          body={(rowData) => formatDateTime(rowData.submittedAt)}
          header="הוגש בתאריך"
          sortable
          style={{ textAlign: 'right' }}
        />
      </DataTable>
    </div>
  );
});
